import React, { useMemo } from "react";
import Highcharts from "highcharts";
import { renderToString } from "react-dom/server";
import { Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMandatorContext } from "@rentablo/theming";
import { toAmericanShort } from "@helpers";
import { getCurrencySymbol } from "@helpers/currencyCodeToSymbol";
import {
  DividendsTooltip,
  type Point as DividendsTooltipPoint,
} from "@components/Chart/highcharts/DividendsTooltip";
import {
  getCommonHighchartsOptions,
  getTickAmount,
} from "../commonChartOptions";
import { DividendChartData } from "./DividendsChart";

export const useHighchartsOptions = (
  chartData: DividendChartData[],
  theme: Theme,
  currency: string,
  height?: number
): Highcharts.Options => {
  const { t } = useTranslation();
  const { mandatorSlug } = useMandatorContext();
  const commonHighchartsOptions = useMemo(
    () => getCommonHighchartsOptions(theme),
    [theme]
  );

  return useMemo(() => {
    return {
      credits: {
        enabled: false,
      },
      chart: {
        type: "column",
        ...commonHighchartsOptions.chart,
        ...(height ? { height } : {}),
      },
      title: {
        text: "",
      },
      tooltip: {
        ...commonHighchartsOptions.tooltip,
        shared: true,
        outside: true,
        formatter: function () {
          const rawPoint = (this.points?.[0]?.point ??
            this.point) as unknown as DividendChartData;
          const isYearlyView =
            chartData.length > 0 && chartData.every((p) => p.month === 0);

          const tooltipPoint: DividendsTooltipPoint = {
            year: rawPoint.year,
            index: rawPoint.month ?? 0,
            y: (rawPoint.historicY ?? 0) + (rawPoint.predictedY ?? 0),
            dividends: [
              ...(rawPoint.historicDividends ?? []).map((d) => ({
                amount: d.amount ?? 0,
                name: d.invRefName ?? "",
                isPrediction: false,
              })),
              ...(rawPoint.predictedDividends ?? []).map((d) => ({
                amount: d.amount ?? 0,
                name: d.invRefName ?? "",
                isPrediction: true,
              })),
            ],
          };

          return renderToString(
            <DividendsTooltip
              point={tooltipPoint}
              theme={theme}
              mode={isYearlyView ? "short" : "long"}
              mandatorSlug={mandatorSlug}
              receivedColor={theme.palette.graphVisual["02"].mid}
              predictedColor={theme.palette.graphVisual["02"].mid}
              currency={currency}
            />
          );
        },
      },
      xAxis: {
        type: "category",
        lineColor: theme.palette.border.secondary,
        labels: {
          formatter: (data) => {
            // Strip year suffix for display (e.g., "Mär 25" → "Mär")
            const displayName = (data.value as string).split(" ")[0];
            return renderToString(
              <div
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  textTransform: "uppercase",
                }}
              >
                {displayName}
              </div>
            );
          },
        },
        title: {
          text: "",
        },
      },
      yAxis: {
        reversedStacks: false,
        margin: 0,
        tickAmount: getTickAmount(height),
        title: {
          text: "",
        },
        gridLineColor: theme.palette.border.secondary,
        labels: {
          useHTML: true,
          formatter: (data) => {
            return renderToString(
              <div
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                }}
              >
                {getCurrencySymbol(currency)}
                {toAmericanShort(data.value as number, 0, 2)}
              </div>
            );
          },
        },
      },
      legend: {
        ...commonHighchartsOptions.legend,
        symbolWidth: 0,
        symbolHeight: 0,
        symbolPadding: 0,
        labelFormatter: function () {
          const dividendColor = theme.palette.graphVisual["02"].mid;
          const isPredicted =
            this.name ===
            t("dashboardPage.dashboard.performance.tabs.dividend.predicted");
          const symbolStyle: React.CSSProperties = isPredicted
            ? {
                width: 8,
                height: 8,
                borderRadius: "50%",
                border: `1.5px dashed ${dividendColor}`,
                boxSizing: "border-box",
              }
            : {
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: dividendColor,
              };
          return renderToString(
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFamily: theme.typography.fontFamily as string,
                fontSize: theme.typography.body3.fontSize as string,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                lineHeight: "18px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  ...symbolStyle,
                }}
              />
              {this.name}
            </div>
          );
        },
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        column: {
          stacking: "normal",
          legendSymbol: "rectangle",
          borderRadius: { radius: 2, where: "end", scope: "point" },
          borderWidth: 1,
          dataLabels: {
            enabled: false,
            style: {
              fontFamily: "var(--ff-font-family-base)",
              fontWeight: "400",
              textOutline: "0",
              fontSize: "16px",
            },
          },
          pointStart: 1,
          pointWidth: 10,
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
      },
      navigation: {
        buttonOptions: {
          y: 12,
        },
      },
      series: [
        {
          name: t("dashboardPage.dashboard.performance.tabs.dividend.title"),
          data: chartData.map(({ color, ...d }) => {
            const isEmpty = !d.historicY && !d.predictedY;
            const pointColor = isEmpty
              ? theme.palette.graphVisual["08"].subtle
              : theme.palette.graphVisual["02"].mid;
            return {
              ...d,
              y: isEmpty ? 0 : d.historicY > 0 ? d.historicY : null,
              color: pointColor,
              borderColor: pointColor,
            };
          }),
          type: "column",
          color: theme.palette.graphVisual["02"].mid,
          borderColor: theme.palette.graphVisual["02"].mid,
          minPointLength: 3,
        },
        {
          name: t(
            "dashboardPage.dashboard.performance.tabs.dividend.predicted"
          ),
          data: chartData.map(({ color, ...d }) => ({
            ...d,
            y: d.predictedY > 0 ? d.predictedY : null,
            color: theme.palette.background.paper,
          })),
          type: "column",
          color: theme.palette.graphVisual["02"].mid,
          borderColor: theme.palette.graphVisual["02"].mid,
          borderWidth: 1,
          dashStyle: "Dash",
        },
      ],
    };
  }, [
    chartData,
    theme,
    commonHighchartsOptions,
    t,
    height,
    mandatorSlug,
    currency,
  ]);
};
